import { useMemo } from 'react'
import { BaseElement, Editor, Element, Path, Transforms } from 'slate'
import { RenderElementProps, useSelected } from 'slate-react'
import styled, { css } from 'styled-components/macro'
import { ifProp } from 'styled-tools'
import isUrl from 'is-url'

import { AtOptions } from './helpers'
import { BlockElement } from './withBlocks'

interface BaseEmbedBlock extends BaseElement {
    type: 'embed'
}

interface VimeoEmbedBlock extends BaseEmbedBlock {
    kind: 'vimeo'
    url: string
}

interface MediaEmbedBlock extends BaseEmbedBlock {
    kind: 'media'
    url: string
}

const SUPPORTED_MEDIA_TYPES = ['jpg', 'jpeg', 'png']

export type EmbedBlock = VimeoEmbedBlock | MediaEmbedBlock

interface WithEmbedsOptions {
    defaultType: BlockElement['type']
}

export function withEmbeds({ defaultType }: WithEmbedsOptions) {
    return (editor: Editor) => {
        const { isVoid, insertBreak, normalizeNode, insertData, deleteBackward, deleteForward } = editor

        editor.isVoid = (el) => EmbedBlock.isEmbed(el) || isVoid(el)

        editor.insertBreak = () => {
            if (EmbedBlock.hasEmbed(editor)) {
                const [[, embedPath]] = Editor.nodes(editor, { match: EmbedBlock.isEmbed })
                Transforms.insertNodes(editor, { type: defaultType, children: [{ text: '' }] }, { at: embedPath })
                return
            }

            insertBreak()
        }

        editor.normalizeNode = (entry) => {
            const [node, path] = entry

            if (EmbedBlock.isEmbed(node) && !Editor.next(editor, { at: path })) {
                Transforms.insertNodes(editor, { type: defaultType, children: [{ text: '' }] }, { at: Path.next(path) })
                return
            }

            normalizeNode(entry)
        }

        editor.deleteBackward = (unit) => {
            if (EmbedBlock.hasEmbed(editor)) {
                Transforms.removeNodes(editor, { match: EmbedBlock.isEmbed })
                Transforms.move(editor, { reverse: true })
                return
            }

            deleteBackward(unit)
        }

        editor.deleteForward = (unit) => {
            if (EmbedBlock.hasEmbed(editor)) {
                Transforms.removeNodes(editor, { match: EmbedBlock.isEmbed })
                return
            }

            deleteForward(unit)
        }

        editor.insertData = (data) => {
            const text = data.getData('text/plain')

            const node = EmbedBlock.createEmbed(text)
            if (node) {
                Transforms.insertNodes(editor, node)
                return
            }

            insertData(data)
        }

        return editor
    }
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const EmbedBlock = {
    isEmbed(elem: any): elem is EmbedBlock {
        return Element.isElement(elem) && elem.type === 'embed'
    },

    isVimeoEmbed(elem: any): elem is VimeoEmbedBlock {
        return EmbedBlock.isEmbed(elem) && elem.kind === 'vimeo'
    },

    isMediaEmbed(elem: any): elem is MediaEmbedBlock {
        return EmbedBlock.isEmbed(elem) && elem.kind === 'media'
    },

    createEmbed(url: string): EmbedBlock | null {
        if (isUrl(url)) {
            const parsedUrl = new URL(url)
            const ext = parsedUrl.pathname.split('.').pop() ?? ''

            if (parsedUrl.hostname === 'vimeo.com') {
                return {
                    type: 'embed',
                    kind: 'vimeo',
                    url: `https://player.vimeo.com/video${parsedUrl.pathname}`,
                    children: [{ text: '' }],
                }
            } else if (SUPPORTED_MEDIA_TYPES.includes(ext)) {
                return {
                    type: 'embed',
                    kind: 'media',
                    url,
                    children: [{ text: '' }],
                }
            }
        }

        return null
    },

    hasEmbed(editor: Editor, options: AtOptions = {}) {
        const { at = editor.selection } = options
        if (!at) return

        const [match] = Editor.nodes(editor, { at, match: EmbedBlock.isEmbed })
        return !!match
    },

    hasVimeoEmbed(editor: Editor, options: AtOptions = {}) {
        const { at = editor.selection } = options
        if (!at) return

        const [match] = Editor.nodes(editor, { at, match: EmbedBlock.isVimeoEmbed })
        return !!match
    },

    hasMediaEmbed(editor: Editor, options: AtOptions = {}) {
        const { at = editor.selection } = options
        if (!at) return

        const [match] = Editor.nodes(editor, { at, match: EmbedBlock.isMediaEmbed })
        return !!match
    },
}

const Wrapper = styled.div``

const EmbedContainer = styled.div<{ isSelected: boolean }>`
    display: flex;
    justify-content: center;

    padding: 8px;
    border-radius: 4px;

    ${ifProp(
        'isSelected',
        css`
            padding: 6px;
            border: 2px solid #3498db;
        `,
    )}
`

interface EmbedViewProps extends RenderElementProps {
    element: EmbedBlock
}

export function EmbedView({ attributes, children, element }: EmbedViewProps) {
    const isSelected = useSelected()
    const embed = useMemo(() => {
        switch (element.kind) {
            case 'vimeo':
                return <VimeoEmbedView element={element} />
            case 'media':
                return <MediaEmbedView element={element} />
        }
    }, [element])

    return (
        <Wrapper {...attributes}>
            <EmbedContainer isSelected={isSelected} contentEditable={false}>
                {embed}
            </EmbedContainer>
            {children}
        </Wrapper>
    )
}

interface VimeoEmbedViewProps {
    element: VimeoEmbedBlock
}

export function VimeoEmbedView({ element }: VimeoEmbedViewProps) {
    return (
        <iframe
            title="asdf"
            src={element.url}
            width="640"
            height="360"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen></iframe>
    )
}

interface MediaEmbedViewProps {
    element: MediaEmbedBlock
}

export function MediaEmbedView({ element }: MediaEmbedViewProps) {
    return <img src={element.url} alt="" style={{ maxWidth: '100%' }} />
}
